import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/utils/api";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { useSettings } from "@/contexts/SettingsContext";
import { InvoiceSettings as InvoiceSettingsType, InvoiceTemplate } from "@/types/invoice";
import {
  InvoiceTemplatePreview,
  buildSampleInvoice,
} from "@/components/dashboard/invoices/InvoiceTemplatePreview";

const TEMPLATES: { value: InvoiceTemplate; label: string; preview: string }[] = [
  { value: "classic", label: "Classic", preview: "Serif font, traditional layout" },
  { value: "modern", label: "Modern", preview: "Dark header, clean grid layout" },
  { value: "minimal", label: "Minimal", preview: "Light, typography-focused" },
  { value: "professional", label: "Professional", preview: "Business-standard format" },
  { value: "bold", label: "Bold", preview: "Strong headers, high contrast" },
  { value: "elegant", label: "Elegant", preview: "Sophisticated, refined style" },
  { value: "formal", label: "Formal", preview: "Traditional corporate format" },
  { value: "compact", label: "Compact", preview: "Dense layout, all on one page" },
  { value: "corporate", label: "Corporate", preview: "Enterprise-ready design" },
  { value: "simple", label: "Simple", preview: "No-frills clean design" },
];

export function InvoiceSettings() {
  const { storeId, currentStore } = useCurrentStore();
  const { storeDetails } = useSettings();
  const queryClient = useQueryClient();

  const [defaultTemplate, setDefaultTemplate] = useState<InvoiceTemplate>("classic");
  const [invoicePrefix, setInvoicePrefix] = useState("INV");
  const [defaultTaxRate, setDefaultTaxRate] = useState(0);
  const [defaultPaymentTerms, setDefaultPaymentTerms] = useState(14);
  const [defaultNotes, setDefaultNotes] = useState("");
  const [defaultTerms, setDefaultTerms] = useState("");

  const { isLoading, data: settingsData } = useQuery<InvoiceSettingsType>({
    queryKey: ["invoice-settings", storeId],
    queryFn: async () => {
      const { data } = await api.get("/api/settings");
      return data.invoiceSettings;
    },
    enabled: !!storeId,
  });

  useEffect(() => {
    if (settingsData) {
      setDefaultTemplate(settingsData.defaultTemplate || "classic");
      setInvoicePrefix(settingsData.invoicePrefix || "INV");
      setDefaultTaxRate(settingsData.defaultTaxRate || 0);
      setDefaultPaymentTerms(settingsData.defaultPaymentTerms || 14);
      setDefaultNotes(settingsData.defaultNotes || "");
      setDefaultTerms(settingsData.defaultTerms || "");
    }
  }, [settingsData]);

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: async () => {
      const { data } = await api.patch("/api/settings/invoice-settings", {
        defaultTemplate,
        invoicePrefix,
        defaultTaxRate: Number(defaultTaxRate),
        defaultPaymentTerms: Number(defaultPaymentTerms),
        defaultNotes,
        defaultTerms,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoice-settings"] });
      // Invalidate all cached invoices so any open view page picks up the new default template
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["invoice"] });
      toast.success("Invoice settings saved");
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || "Failed to save settings");
    },
  });

  const selectedTemplate = TEMPLATES.find((t) => t.value === defaultTemplate) || TEMPLATES[0];
  const sampleInvoice = useMemo(
    () =>
      buildSampleInvoice({
        templateId: defaultTemplate,
        prefix: invoicePrefix,
        taxRate: defaultTaxRate,
        paymentTerms: defaultPaymentTerms,
        notes: defaultNotes,
        terms: defaultTerms,
        currency: storeDetails?.currency || currentStore?.currency || "NGN",
      }),
    [
      defaultTemplate,
      invoicePrefix,
      defaultTaxRate,
      defaultPaymentTerms,
      defaultNotes,
      defaultTerms,
      storeDetails?.currency,
      currentStore?.currency,
    ],
  );
  const sampleBusiness = useMemo(
    () => ({
      name: storeDetails?.storeName || currentStore?.storeName || "Your store",
      logo: storeDetails?.logo || currentStore?.logo || "",
      currency: storeDetails?.currency || currentStore?.currency || "NGN",
      address: [
        storeDetails?.address?.street,
        storeDetails?.address?.city,
        storeDetails?.address?.state,
        storeDetails?.address?.country,
      ]
        .filter(Boolean)
        .join(", "),
      phone: storeDetails?.whatsappNumber || "",
      website: storeDetails?.socialLinks?.website || "",
      email: "",
    }),
    [storeDetails, currentStore?.storeName, currentStore?.logo, currentStore?.currency],
  );

  if (isLoading) {
    return <div className="py-4 text-sm text-muted-foreground">Loading settings…</div>;
  }

  return (
    <div className="space-y-6 mt-4">
      {/* Template selection */}
      <div>
        <Label className="text-sm font-medium">Default Invoice Template</Label>
        <p className="text-xs text-muted-foreground mb-3">This template will be pre-selected when creating new invoices.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {TEMPLATES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setDefaultTemplate(t.value)}
              className={`border rounded-lg p-3 text-left transition-all ${
                defaultTemplate === t.value
                  ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <div className="font-medium text-sm">{t.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t.preview}</div>
            </button>
          ))}
        </div>

        <div className="mt-4 overflow-hidden rounded-xl border border-[#EBEBEB] bg-[#F5F5F3]">
          <div className="flex items-center justify-between gap-3 border-b border-[#EBEBEB] bg-white px-4 py-2.5">
            <div>
              <div className="text-sm font-medium">{selectedTemplate.label} sample</div>
              <div className="text-xs text-muted-foreground">
                Preview with your store details. This is not a real invoice.
              </div>
            </div>
            <span className="shrink-0 rounded-full bg-[#EDE4FB] px-2.5 py-0.5 text-[11px] font-medium text-primary">
              Sample
            </span>
          </div>
          <div className="p-3 sm:p-4">
            <div className="overflow-hidden rounded-lg border border-[#EBEBEB] bg-white shadow-sm">
              <InvoiceTemplatePreview invoice={sampleInvoice} business={sampleBusiness} />
            </div>
          </div>
        </div>
      </div>

      {/* Invoice numbering */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs">Invoice Prefix</Label>
          <Input
            value={invoicePrefix}
            onChange={(e) => setInvoicePrefix(e.target.value.toUpperCase())}
            placeholder="INV"
            maxLength={10}
            className="mt-1"
          />
          <p className="text-xs text-muted-foreground mt-1">e.g. INV → INV-0001</p>
        </div>
        <div>
          <Label className="text-xs">Default Tax Rate (%)</Label>
          <Input
            type="number"
            min={0}
            max={100}
            step="0.1"
            value={defaultTaxRate}
            onChange={(e) => setDefaultTaxRate(Number(e.target.value))}
            className="mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Default Payment Terms (days)</Label>
          <Input
            type="number"
            min={0}
            value={defaultPaymentTerms}
            onChange={(e) => setDefaultPaymentTerms(Number(e.target.value))}
            className="mt-1"
          />
          <p className="text-xs text-muted-foreground mt-1">Due date = issue date + this many days</p>
        </div>
      </div>

      {/* Default notes & terms */}
      <div className="space-y-4">
        <div>
          <Label className="text-xs">Default Notes</Label>
          <Textarea
            value={defaultNotes}
            onChange={(e) => setDefaultNotes(e.target.value)}
            placeholder="Pre-filled notes on every new invoice…"
            className="mt-1 resize-none"
            rows={3}
          />
        </div>
        <div>
          <Label className="text-xs">Default Terms & Conditions</Label>
          <Textarea
            value={defaultTerms}
            onChange={(e) => setDefaultTerms(e.target.value)}
            placeholder="Pre-filled terms on every new invoice…"
            className="mt-1 resize-none"
            rows={3}
          />
        </div>
      </div>

      <Button onClick={() => save()} disabled={isSaving}>
        {isSaving ? "Saving…" : "Save Invoice Settings"}
      </Button>
    </div>
  );
}
